import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import { initDropdownCheck, type DropdownCheckHandle } from "../components/dropdown-check";
import type { Practice, Department, DepartmentGroup, Student } from "../types";
import L from "leaflet";
import { addBasemap } from "../utils/basemap";
import { esc } from "../utils/html";

let map: L.Map | null = null;
let practices: Practice[] = [];
let departments: Department[] = [];
let allGroups: DepartmentGroup[] = [];
let kapazitaetProZeitraumEnabled = false;
let markers: { practice: Practice; marker: L.CircleMarker; inRange: boolean }[] = [];
let searchMarker: L.Marker | null = null;
let rangeCircle: L.Circle | null = null;
let searchLat: number | null = null;
let searchLng: number | null = null;
let searchLabel = "Gesuchte Adresse";
let rangeKm = 5;
// practiceId → priority (0 = not wished)
let wishPracticeIds: Map<number, number> = new Map();
// Verwirft verspätete Wunsch-Antworten, wenn sich die Auswahl inzwischen geändert hat
let focusToken = 0;

// Schüler / Klassen
type Mode = "student" | "classes";
let mode: Mode = "student";
let allStudents: Student[] = [];
// Klassen in Anzeigereihenfolge; Schlüssel = classId oder "none" für Schüler ohne Klasse
let classKeys: string[] = [];
let classInfo: Map<string, { label: string; color: string }> = new Map();
let selectedClassKeys: string[] = [];
let selectedStudentIds: Set<string> = new Set();
let studentMarkers: L.Marker[] = [];
let legend: L.Control | null = null;
// Klassen-Modus mit mehreren Schülern: Stelle, um die der Umkreis liegt
let centerPractice: Practice | null = null;
let classesHandle: DropdownCheckHandle | null = null;
let pageEl: HTMLElement | null = null;
let studentsHandle: DropdownCheckHandle | null = null;

// Filter state
let filterCategories: string[] = [];
let filterDepartments: string[] = [];

const GERMANY_CENTER: L.LatLngExpression = [51.1657, 10.4515];
const DEFAULT_ZOOM = 6;
const HIGHLIGHT_COLOR = "#2563eb";
// Goldgelb statt Knallgelb: auf der hellen Karte braucht der Punkt den dunklen Rand
const NORMAL_COLOR = "#eab308";
const NORMAL_BORDER = "#854d0e";
const IN_RANGE_COLOR = "#22c55e";
const OUT_RANGE_COLOR = "#9ca3af";
const WISH_COLOR = "#9333ea";
const WISH_OUT_COLOR = "#c084fc";
// Klassenfarben meiden Gelb (Stellen), Grün/Grau (Umkreis) und Lila (Wünsche)
const CLASS_COLORS = ["#2563eb", "#dc2626", "#db2777", "#0891b2", "#0f766e", "#4338ca", "#c2410c", "#9f1239"];

async function load() {
  const [p, d, s, g] = await Promise.all([api.practices.list(), api.departments(), api.settings.get(), api.settings.groupsWithOverrides()]);
  practices = p; departments = d;
  kapazitaetProZeitraumEnabled = s.kapazitaetProZeitraumEnabled ?? false;
  allGroups = g;
}

function getMarkerColor(p: Practice, inRange: boolean): string {
  if (searchLat !== null) {
    if (wishPracticeIds.has(+p.id)) return inRange ? WISH_COLOR : WISH_OUT_COLOR;
    return inRange ? IN_RANGE_COLOR : OUT_RANGE_COLOR;
  }
  if (isHighlightedByFilter(p)) return HIGHLIGHT_COLOR;
  return NORMAL_COLOR;
}

function getMarkerRadius(p: Practice, inRange: boolean): number {
  if (wishPracticeIds.has(+p.id) || isCenter(p)) return 12;
  if (searchLat !== null && inRange) return 10;
  if (isHighlightedByFilter(p)) return 10;
  return 8;
}

function isHighlightedByFilter(p: Practice): boolean {
  const catMatch = filterCategories.length === 0 || filterCategories.includes(p.category || "PRAXIS");
  const deptMatch = filterDepartments.length === 0 || filterDepartments.some(d => (p.capacities[d] || 0) > 0);
  return catMatch && deptMatch && (filterCategories.length > 0 || filterDepartments.length > 0);
}

function matchesFilter(p: Practice): boolean {
  const catMatch = filterCategories.length === 0 || filterCategories.includes(p.category || "PRAXIS");
  const deptMatch = filterDepartments.length === 0 || filterDepartments.some(d => (p.capacities[d] || 0) > 0);
  return catMatch && deptMatch;
}

function distanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function isCenter(p: Practice): boolean {
  return centerPractice !== null && centerPractice.id == p.id;
}

function hasGeo(s: Student): boolean {
  return !!(s.latitude && s.longitude);
}

function classKey(s: Student): string {
  return s.classId != null ? String(s.classId) : "none";
}

function classColor(s: Student): string {
  return classInfo.get(classKey(s))?.color || CLASS_COLORS[0];
}

/** Im Klassen-Modus gewählte Schüler mit Koordinaten, in Listenreihenfolge. */
function selectedWithGeo(): Student[] {
  if (mode !== "classes") return [];
  return allStudents.filter(s => selectedStudentIds.has(String(s.id)) && hasGeo(s));
}

/** Ab zwei Schülern zeigt der Klassen-Modus Pins statt eines Mittelpunkts. */
function isMultiStudentView(): boolean {
  return selectedWithGeo().length >= 2;
}

function isInCenterRange(s: Student): boolean {
  return centerPractice !== null
    && distanceKm(centerPractice.latitude!, centerPractice.longitude!, s.latitude!, s.longitude!) <= rangeKm;
}

function readRange(el: HTMLElement): number {
  return parseInt((el.querySelector("#search-range") as HTMLInputElement).value) || 5;
}

function initMap(container: HTMLElement) {
  map = L.map(container, { zoomControl: true }).setView(GERMANY_CENTER, DEFAULT_ZOOM);
  addBasemap(map);
}

function updateMarkers() {
  if (!map) return;

  markers.forEach(m => m.marker.remove());
  markers = [];

  const validPractices = practices.filter(p => p.latitude && p.longitude && matchesFilter(p));

  validPractices.forEach(p => {
    const inRange = searchLat !== null
      ? distanceKm(searchLat!, searchLng!, p.latitude!, p.longitude!) <= rangeKm
      : false;

    const wishPrio = wishPracticeIds.get(+p.id);
    const center = isCenter(p);
    const fillColor = getMarkerColor(p, inRange);
    const marker = L.circleMarker([p.latitude!, p.longitude!], {
      radius: getMarkerRadius(p, inRange),
      fillColor,
      color: wishPrio ? WISH_COLOR : center ? HIGHLIGHT_COLOR : fillColor === NORMAL_COLOR ? NORMAL_BORDER : "#fff",
      weight: wishPrio || center ? 3 : 2,
      fillOpacity: (searchLat !== null && !inRange && !wishPrio) ? 0.4 : 0.9,
    }).addTo(map!);
    if (center) marker.bringToFront();

    const catLabel = p.category === "KRANKENHAUS" ? "KH" : "Praxis";
    const wishTip = wishPrio ? `<br><span style="color:${WISH_COLOR}">★ Wunsch (Priorität ${wishPrio})</span>` : "";
    marker.bindTooltip(`<strong>${esc(p.name)}</strong><br>${esc(p.plz || "")} ${esc(p.ort || "")} · ${catLabel}${wishTip}`, { direction: "top", offset: [0, -10] });
    marker.on("click", () => onPracticeClick(p));

    markers.push({ practice: p, marker, inRange });
  });

  // Nur auf die Stellen zoomen, solange nichts anderes den Ausschnitt bestimmt
  if (validPractices.length > 0 && !searchLat && !centerPractice && !isMultiStudentView()) {
    const group = L.featureGroup(markers.map(m => m.marker));
    map.fitBounds(group.getBounds().pad(0.1));
  }
}

function studentIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: "student-pin-icon",
    html: `<svg width="22" height="30" viewBox="0 0 22 30" aria-hidden="true"><path d="M11 1C5.5 1 1 5.4 1 10.9 1 18.3 11 29 11 29s10-10.7 10-18.1C21 5.4 16.5 1 11 1z" fill="${color}" stroke="#fff" stroke-width="2"/><circle cx="11" cy="11" r="3.5" fill="#fff"/></svg>`,
    iconSize: [22, 30],
    iconAnchor: [11, 29],
    tooltipAnchor: [0, -26],
  });
}

function updateStudentMarkers() {
  studentMarkers.forEach(m => m.remove());
  studentMarkers = [];
  if (legend) { legend.remove(); legend = null; }
  if (!map || !isMultiStudentView()) return;

  const list = selectedWithGeo();
  list.forEach(s => {
    const inRange = isInCenterRange(s);
    const faded = centerPractice !== null && !inRange;
    const marker = L.marker([s.latitude!, s.longitude!], {
      icon: studentIcon(classColor(s)),
      opacity: faded ? 0.35 : 1,
      zIndexOffset: faded ? 0 : 500,
    }).addTo(map!);
    const place = [s.plz, s.ort].filter(Boolean).join(" ");
    const cls = classInfo.get(classKey(s))?.label || "";
    marker.bindTooltip(`<strong>${esc(s.firstName)} ${esc(s.lastName)}</strong><br>${esc(cls)}${place ? ` · ${esc(place)}` : ""}`, { direction: "top" });
    studentMarkers.push(marker);
  });

  // Legende: Klassen der gewählten Schüler plus das Zeichen für Praktikumsstellen
  const counts = new Map<string, number>();
  list.forEach(s => counts.set(classKey(s), (counts.get(classKey(s)) || 0) + 1));
  const rows = classKeys.filter(k => counts.has(k)).map(k => {
    const info = classInfo.get(k)!;
    return `<div class="map-legend-row"><span class="map-legend-pin" style="background:${info.color}"></span>${esc(info.label)} (${counts.get(k)})</div>`;
  }).join("");
  const div = L.DomUtil.create("div", "map-legend");
  div.innerHTML = rows + `<div class="map-legend-row"><span class="map-legend-dot"></span>Praktikumsstelle</div>`;
  legend = new L.Control({ position: "bottomleft" });
  legend.onAdd = () => div;
  legend.addTo(map);
}

/** Infozeile über der Liste: wie viele der gewählten Schüler im Umkreis der Stelle wohnen. */
function updateCenterInfo(el: HTMLElement) {
  const box = el.querySelector("#map-center-info") as HTMLElement | null;
  if (!box) return;
  if (!centerPractice) { box.hidden = true; box.innerHTML = ""; return; }

  const list = selectedWithGeo();
  const inside = list.filter(isInCenterRange);
  const perClass = classKeys
    .filter(k => list.some(s => classKey(s) === k))
    .map(k => `${esc(classInfo.get(k)!.label)}: ${inside.filter(s => classKey(s) === k).length}`);
  const breakdown = perClass.length > 1 ? ` (${perClass.join(", ")})` : "";

  box.innerHTML = `
    <div><strong>${esc(centerPractice.name)}</strong>: ${inside.length} von ${list.length} Schülern im Umkreis von ${rangeKm} km${breakdown}</div>
    <div class="map-center-info-actions">
      <button type="button" class="link-btn" data-act="details">Details</button>
      <button type="button" class="link-btn" data-act="clear-center">Umkreis aufheben</button>
    </div>`;
  box.hidden = false;
}

function onPracticeClick(p: Practice, fromList = false) {
  if (isMultiStudentView() && p.latitude && p.longitude && pageEl) {
    toggleCenterPractice(p, pageEl);
    return;
  }
  showPracticeDetail(p);
  if (fromList && map && p.latitude && p.longitude) map.setView([p.latitude, p.longitude], 14);
}

function toggleCenterPractice(p: Practice, el: HTMLElement) {
  centerPractice = isCenter(p) ? null : p;
  rangeKm = readRange(el);
  updateSearch(el);
}

function updateSidebar(el: HTMLElement) {
  const listEl = el.querySelector("#map-practice-list") as HTMLElement;
  if (!listEl) return;

  const filtered = practices.filter(matchesFilter);

  const withDistance = filtered.map(p => {
    const hasGeo = !!(p.latitude && p.longitude);
    const distance = (searchLat !== null && hasGeo) ? distanceKm(searchLat!, searchLng!, p.latitude!, p.longitude!) : null;
    return {
      practice: p,
      hasGeo,
      distance,
      inRange: distance !== null && distance <= rangeKm,
    };
  });

  if (searchLat !== null) {
    // Erst nach Abschnitt, dann Wünsche nach vorn. Die Trenner unten setzen voraus, dass
    // jeder Abschnitt zusammenhängend steht: Sortierte man Wünsche vor den Abschnitt, zöge
    // ein Wunsch außerhalb den Trenner vor alle übrigen Stellen im Umkreis.
    const section = (x: typeof withDistance[number]) => !x.hasGeo ? 2 : x.inRange ? 0 : 1;
    withDistance.sort((a, b) => {
      if (section(a) !== section(b)) return section(a) - section(b);
      const aPrio = wishPracticeIds.get(+a.practice.id);
      const bPrio = wishPracticeIds.get(+b.practice.id);
      if ((aPrio !== undefined) !== (bPrio !== undefined)) return aPrio !== undefined ? -1 : 1;
      if (aPrio !== undefined && bPrio !== undefined && aPrio !== bPrio) return aPrio - bPrio;
      return (a.distance || 0) - (b.distance || 0);
    });
  } else {
    withDistance.sort((a, b) => {
      if (a.hasGeo && !b.hasGeo) return -1;
      if (!a.hasGeo && b.hasGeo) return 1;
      return a.practice.name.localeCompare(b.practice.name);
    });
  }

  let html = "";
  let separatorAdded = false;
  let noGeoSeparatorAdded = false;

  withDistance.forEach(item => {
    if (searchLat !== null && !separatorAdded && !item.inRange && item.hasGeo) {
      html += `<div class="map-list-separator">Ausserhalb des Umkreises</div>`;
      separatorAdded = true;
    }
    if (!noGeoSeparatorAdded && !item.hasGeo) {
      html += `<div class="map-list-separator">Ohne Koordinaten</div>`;
      noGeoSeparatorAdded = true;
    }

    const highlighted = isHighlightedByFilter(item.practice);
    const wishPrio = wishPracticeIds.get(+item.practice.id);
    const distText = item.distance !== null ? `<span class="map-list-distance">${item.distance.toFixed(1)} km</span>` : "";
    const catLabel = item.practice.category === "KRANKENHAUS" ? "Krankenhaus" : "Praxis";
    const tags = departments
      .filter(d => (item.practice.capacities[d.id] || 0) > 0)
      .map(d => `<span class="dept-tag" style="background:${d.color}">${d.shortName}: ${item.practice.capacities[d.id]}</span>`)
      .join("");
    const wishBadge = wishPrio
      ? `<span style="font-size:.75rem;font-weight:600;color:${WISH_COLOR};margin-left:.25rem">★ Wunsch ${wishPrio}</span>`
      : "";

    html += `<div class="map-list-item ${item.inRange ? 'in-range' : ''} ${highlighted ? 'highlighted' : ''} ${!item.hasGeo ? 'no-geo' : ''} ${isCenter(item.practice) ? 'is-center' : ''}" data-id="${item.practice.id}">
      <div class="map-list-item-header">
        <strong>${esc(item.practice.name)}</strong>${wishBadge}
        ${distText}
      </div>
      <div class="map-list-item-meta">${esc(item.practice.plz || "")} ${esc(item.practice.ort || "")} · ${catLabel}</div>
      <div class="map-list-item-tags">${tags}</div>
    </div>`;
  });

  if (withDistance.length === 0) {
    html = `<div class="empty-state"><p>Keine Praktikumsstellen vorhanden.</p></div>`;
  }

  listEl.innerHTML = html;

  listEl.querySelectorAll(".map-list-item").forEach(item => {
    item.addEventListener("click", () => {
      const id = +(item as HTMLElement).dataset.id!;
      const p = practices.find(pr => pr.id == id);
      if (p) onPracticeClick(p, true);
    });
  });
}

function showPracticeDetail(p: Practice) {
  const deptRow = (d: Department) => {
    const ppMax = p.perPeriodCapacities?.[d.id];
    const ppText = (kapazitaetProZeitraumEnabled && ppMax)
      ? ` <span style="color:var(--gray-400);font-size:.75rem">· max. ${ppMax}/Zeitraum</span>`
      : "";
    return `<tr><td style="color:${d.color};font-weight:500">${d.nameDe}</td><td>${p.capacities[d.id]} Plätze${ppText}</td></tr>`;
  };

  const depsWithCap = departments.filter(d => (p.capacities[d.id] || 0) > 0);
  const shownGroups = allGroups.filter(g => g.isActive !== false);
  const grouped = shownGroups
    .map(g => ({ g, depts: depsWithCap.filter(d => g.departments.some(x => x.id === d.id)) }))
    .filter(x => x.depts.length > 0);
  const ungrouped = depsWithCap.filter(d => !shownGroups.some(g => g.departments.some(x => x.id === d.id)));

  let capsHtml = "";
  if (depsWithCap.length > 0) {
    capsHtml = grouped.map(({ g, depts }) => `
      <tr><td colspan="2" style="font-size:.75rem;font-weight:600;text-transform:uppercase;letter-spacing:.06em;color:var(--gray-500);padding-top:.75rem;padding-bottom:.25rem">${esc(g.name)}</td></tr>
      ${depts.map(deptRow).join("")}`).join("") +
      (ungrouped.length > 0 ? `
      <tr><td colspan="2" style="font-size:.75rem;font-weight:600;text-transform:uppercase;letter-spacing:.06em;color:var(--gray-500);padding-top:.75rem;padding-bottom:.25rem">Weitere Fachbereiche</td></tr>
      ${ungrouped.map(deptRow).join("")}` : "");
  }

  const catLabel = p.category === "KRANKENHAUS" ? "Krankenhaus" : "Praxis";

  openModal(p.name, `
    <div class="form-grid">
      <div class="form-group"><label>Kategorie</label><div>${catLabel}</div></div>
      <div class="form-group"><label>Adresse</label><div>${esc(p.street || "")} ${esc(p.houseNo || "")}<br>${esc(p.plz || "")} ${esc(p.ort || "")}</div></div>
      ${p.leadingContact ? `<div class="form-group"><label>Therapeutische Leitung</label><div>${esc(p.leadingContact)}${p.leadingContactPhone ? `<br>Tel: ${esc(p.leadingContactPhone)}` : ""}${p.leadingContactEmail ? `<br>${esc(p.leadingContactEmail)}` : ""}</div></div>` : ""}
      ${p.instructor ? `<div class="form-group"><label>Anleiter</label><div>${esc(p.instructor)}${p.instructorPhone ? `<br>Tel: ${esc(p.instructorPhone)}` : ""}${p.instructorEmail ? `<br>${esc(p.instructorEmail)}` : ""}</div></div>` : ""}
      ${p.contactPersonStudents ? `<div class="form-group"><label>Kontaktperson für Schüler</label><div>${esc(p.contactPersonStudents)}${p.phoneStudents ? `<br>Tel: ${esc(p.phoneStudents)}` : ""}${p.emailStudents ? `<br>${esc(p.emailStudents)}` : ""}</div></div>` : ""}
      ${p.commentStudents ? `<div class="form-group col-span-2"><label>Kommentar</label><div>${esc(p.commentStudents)}</div></div>` : ""}
      ${p.visitingHours ? `<div class="form-group"><label>Sichtstunden</label><div>${esc(p.visitingHours)}</div></div>` : ""}
      ${p.travelTimeMinutes ? `<div class="form-group"><label>Fahrzeit</label><div>${esc(p.travelTimeMinutes)} Min.</div></div>` : ""}
      ${p.importantChanges ? `<div class="form-group col-span-2"><label>Wichtige Änderungen</label><div>${esc(p.importantChanges)}</div></div>` : ""}
    </div>
    ${capsHtml ? `<fieldset style="margin-top:1rem"><legend>Kapazitäten</legend><table class="detail-table">${capsHtml}</table></fieldset>` : ""}
  `, { wide: true });
}

function updateSearch(el: HTMLElement) {
  if (!map) return;

  if (searchMarker) { searchMarker.remove(); searchMarker = null; }
  if (rangeCircle) { rangeCircle.remove(); rangeCircle = null; }

  const center: [number, number] | null = searchLat !== null && searchLng !== null
    ? [searchLat, searchLng]
    : centerPractice ? [centerPractice.latitude!, centerPractice.longitude!] : null;

  if (searchLat !== null && searchLng !== null) {
    searchMarker = L.marker([searchLat, searchLng], {
      icon: L.divIcon({
        className: "search-marker-icon",
        html: `<div style="background:#ef4444;width:14px;height:14px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      }),
    }).addTo(map);
    searchMarker.bindTooltip(searchLabel, { direction: "top", offset: [0, -10] });
  }

  if (center) {
    rangeCircle = L.circle(center, {
      radius: rangeKm * 1000,
      color: HIGHLIGHT_COLOR,
      fillColor: HIGHLIGHT_COLOR,
      fillOpacity: 0.08,
      weight: 2,
      dashArray: "6 4",
      interactive: false,
    }).addTo(map);
  }

  updateMarkers();
  updateStudentMarkers();
  updateSidebar(el);
  updateCenterInfo(el);

  if (rangeCircle) {
    map.fitBounds(rangeCircle.getBounds().pad(0.1));
  } else if (studentMarkers.length > 0) {
    // Oben Platz für den Kopf der Pins, die über ihrem Punkt stehen
    map.fitBounds(L.featureGroup(studentMarkers).getBounds().pad(0.1), { paddingTopLeft: [0, 30] });
  }
}

/** Ein Schüler als Mittelpunkt: Umkreis um seinen Wohnort, seine Wünsche hervorgehoben. */
async function focusStudent(s: Student, el: HTMLElement) {
  const token = ++focusToken;
  rangeKm = readRange(el);
  centerPractice = null;
  searchLat = s.latitude ?? null;
  searchLng = s.longitude ?? null;
  searchLabel = `${s.firstName} ${s.lastName}`;
  wishPracticeIds.clear();
  try {
    const prefs = await api.schuelerWuensche(s.id);
    if (token !== focusToken) return;
    Object.values(prefs).flat().forEach((p: any) => wishPracticeIds.set(+p.practiceId, p.priority));
  } catch { /* ignorieren */ }
  if (token !== focusToken) return;
  updateSearch(el);
}

/** Leert die Auswahl beider Modi, ohne neu zu zeichnen. */
function clearSelection(el: HTMLElement) {
  focusToken++;
  searchLat = null;
  searchLng = null;
  searchLabel = "Gesuchte Adresse";
  wishPracticeIds.clear();
  centerPractice = null;
  selectedClassKeys = [];
  selectedStudentIds = new Set();
  (el.querySelector("#search-student") as HTMLSelectElement).value = "";
  classesHandle?.setValues([]);
  refreshClassesLabel(el);
  buildStudentPanel(el);
}

function buildClassInfo() {
  const labels = new Map<string, string>();
  allStudents.forEach(s => {
    const k = classKey(s);
    if (!labels.has(k)) labels.set(k, k === "none" ? "Ohne Klasse" : (s.className || "Klasse"));
  });
  // "Ohne Klasse" immer ans Ende
  classKeys = [...labels.keys()].sort((a, b) => {
    if (a === "none") return 1;
    if (b === "none") return -1;
    return labels.get(a)!.localeCompare(labels.get(b)!, "de", { numeric: true });
  });
  classInfo = new Map(classKeys.map((k, i) => [k, { label: labels.get(k)!, color: CLASS_COLORS[i % CLASS_COLORS.length] }]));
}

function refreshClassesLabel(el: HTMLElement) {
  const btn = el.querySelector("#filter-classes .dropdown-check-btn") as HTMLButtonElement | null;
  if (!btn) return;
  const names = classKeys.filter(k => selectedClassKeys.includes(k)).map(k => classInfo.get(k)!.label);
  btn.textContent = names.length === 0 ? "Klassen auswählen"
    : names.length <= 2 ? names.join(", ")
    : `${names.length} Klassen`;
}

function refreshStudentsLabel(el: HTMLElement) {
  const btn = el.querySelector("#filter-class-students .dropdown-check-btn") as HTMLButtonElement | null;
  if (!btn) return;
  const n = selectedWithGeo().length;
  btn.disabled = selectedClassKeys.length === 0;
  btn.textContent = selectedClassKeys.length === 0 ? "Zuerst Klasse wählen"
    : n === 1 ? "1 Schüler" : `${n} Schüler`;
}

/** Baut die Schülerliste der gewählten Klassen neu auf. */
function buildStudentPanel(el: HTMLElement) {
  const panel = el.querySelector("#filter-class-students .dropdown-check-panel") as HTMLElement | null;
  if (!panel) return;
  const keys = classKeys.filter(k => selectedClassKeys.includes(k));
  if (keys.length === 0) {
    panel.innerHTML = "";
    refreshStudentsLabel(el);
    return;
  }
  const item = (s: Student) => {
    const name = `${esc(s.lastName)}, ${esc(s.firstName)}`;
    return hasGeo(s)
      ? `<label class="dropdown-check-item"><input type="checkbox" value="${s.id}" ${selectedStudentIds.has(String(s.id)) ? "checked" : ""} /> ${name}</label>`
      : `<label class="dropdown-check-item is-disabled"><input type="checkbox" value="${s.id}" disabled /> ${name} <span class="dropdown-check-note">ohne Adresse</span></label>`;
  };
  panel.innerHTML = `
    <div class="dropdown-check-actions">
      <button type="button" class="link-btn" data-act="all">Alle</button>
      <button type="button" class="link-btn" data-act="none">Keine</button>
    </div>
    ${keys.map(k => `
      ${keys.length > 1 ? `<div class="dropdown-check-group">${esc(classInfo.get(k)!.label)}</div>` : ""}
      ${allStudents.filter(s => classKey(s) === k).map(item).join("")}
    `).join("")}`;
  refreshStudentsLabel(el);
}

/** Zeichnet die Karte nach einer Änderung der Klassen- oder Schülerauswahl neu. */
function applyClassSelection(el: HTMLElement) {
  refreshStudentsLabel(el);
  const list = selectedWithGeo();
  if (list.length === 1) { focusStudent(list[0], el); return; }
  focusToken++;
  searchLat = null;
  searchLng = null;
  wishPracticeIds.clear();
  if (list.length < 2) centerPractice = null;
  updateSearch(el);
}

function setupClassFilters(el: HTMLElement) {
  const classDrop = el.querySelector("#filter-classes") as HTMLElement;
  const studentDrop = el.querySelector("#filter-class-students") as HTMLElement;
  const studentPanel = studentDrop.querySelector(".dropdown-check-panel") as HTMLElement;

  classesHandle = initDropdownCheck(classDrop, (vals) => {
    const added = vals.filter(k => !selectedClassKeys.includes(k));
    selectedClassKeys = vals;
    // Abgewählte Klassen fallen raus, neu gewählte kommen komplett dazu
    selectedStudentIds = new Set([...selectedStudentIds].filter(id => {
      const s = allStudents.find(st => String(st.id) === id);
      return !!s && vals.includes(classKey(s));
    }));
    allStudents.filter(s => added.includes(classKey(s)) && hasGeo(s)).forEach(s => selectedStudentIds.add(String(s.id)));
    refreshClassesLabel(el);
    buildStudentPanel(el);
    applyClassSelection(el);
  });

  studentsHandle = initDropdownCheck(studentDrop, (vals) => {
    selectedStudentIds = new Set(vals);
    applyClassSelection(el);
  });

  studentPanel.addEventListener("click", (e) => {
    const act = (e.target as HTMLElement).closest("[data-act]") as HTMLElement | null;
    if (!act || !studentsHandle) return;
    const ids = act.dataset.act === "all"
      ? allStudents.filter(s => selectedClassKeys.includes(classKey(s)) && hasGeo(s)).map(s => String(s.id))
      : [];
    studentsHandle.setValues(ids);
    selectedStudentIds = new Set(ids);
    applyClassSelection(el);
  });

  refreshClassesLabel(el);
  buildStudentPanel(el);
}

function setupFilters(el: HTMLElement, signal: AbortSignal) {
  const catDrop = el.querySelector("#filter-category") as HTMLElement;
  const deptDrop = el.querySelector("#filter-department") as HTMLElement;
  if (!catDrop || !deptDrop) return;

  initDropdownCheck(catDrop, (vals) => {
    filterCategories = vals;
    updateMarkers();
    updateSidebar(el);
  });

  initDropdownCheck(deptDrop, (vals) => {
    filterDepartments = vals;
    updateMarkers();
    updateSidebar(el);
  });

  document.addEventListener("click", () => {
    el.querySelectorAll(".dropdown-check.open").forEach(d => d.classList.remove("open"));
  }, { signal });
}

export async function render(el: HTMLElement) {
  (el as any).__renderAC?.abort();
  const ac = new AbortController();
  (el as any).__renderAC = ac;

  // Reset state
  focusToken++;
  searchLat = null;
  searchLng = null;
  searchLabel = "Gesuchte Adresse";
  rangeKm = 5;
  filterCategories = [];
  filterDepartments = [];
  wishPracticeIds.clear();
  mode = "student";
  selectedClassKeys = [];
  selectedStudentIds = new Set();
  centerPractice = null;
  studentMarkers = [];
  legend = null;
  classesHandle = null;
  studentsHandle = null;
  pageEl = el;
  if (map) { map.remove(); map = null; }

  await load();

  allStudents = await api.students.list();
  buildClassInfo();
  const studentsWithGeo = allStudents.filter(hasGeo);
  const studentOptions = studentsWithGeo.map(s =>
    `<option value="${s.id}">${esc(s.lastName)}, ${esc(s.firstName)}${s.plz || s.ort ? ` (${esc([s.plz, s.ort].filter(Boolean).join(" "))})` : ""}</option>`
  ).join("");

  const classCheckboxes = classKeys.length > 0
    ? classKeys.map(k => {
        const info = classInfo.get(k)!;
        const n = allStudents.filter(s => classKey(s) === k).length;
        return `<label class="dropdown-check-item"><input type="checkbox" value="${esc(k)}" /> <span class="map-class-dot" style="background:${info.color}"></span> ${esc(info.label)} <span class="dropdown-check-note">${n}</span></label>`;
      }).join("")
    : `<div class="dropdown-check-hint">Keine Schüler vorhanden</div>`;

  const deptCheckboxes = departments.map(d =>
    `<label class="dropdown-check-item"><input type="checkbox" value="${d.id}" /> <span class="dept-tag" style="background:${d.color}">${d.shortName}</span> ${d.nameDe}</label>`
  ).join("");

  el.innerHTML = `
    <div class="page-header"><h1>Kartenansicht</h1></div>

    <div class="map-toolbar">
      <div class="map-toolbar-section">
        <div class="mode-toggle">
          <button type="button" class="mode-btn active" id="mode-student">Schüler</button>
          <button type="button" class="mode-btn" id="mode-classes">Klassen</button>
        </div>
      </div>
      <div class="map-toolbar-search-group">
        <div id="search-student-section" style="display:flex;gap:.75rem;align-items:flex-end">
          <div class="map-toolbar-section">
            <label>Schüler</label>
            <select id="search-student">
              <option value="">Schüler auswählen</option>
              ${studentOptions}
            </select>
          </div>
        </div>
        <div id="search-classes-section" style="display:none;gap:.75rem;align-items:flex-end">
          <div class="map-toolbar-section">
            <label>Klassen</label>
            <div class="dropdown-check" id="filter-classes">
              <button type="button" class="dropdown-check-btn">Klassen auswählen</button>
              <div class="dropdown-check-panel dropdown-check-panel-left">
                ${classCheckboxes}
              </div>
            </div>
          </div>
          <div class="map-toolbar-section">
            <label>Schüler</label>
            <div class="dropdown-check" id="filter-class-students">
              <button type="button" class="dropdown-check-btn" disabled>Zuerst Klasse wählen</button>
              <div class="dropdown-check-panel dropdown-check-panel-left dropdown-check-panel-tall"></div>
            </div>
          </div>
        </div>
      </div>
      <div class="map-toolbar-section">
        <label>Umkreis</label>
        <div class="input-row"><input type="number" id="search-range" value="5" min="1" max="100" style="width:70px" /><span>km</span></div>
      </div>
      <div class="map-toolbar-section map-toolbar-buttons">
        <button class="btn btn-secondary btn-sm" id="btn-clear-search">Zurücksetzen</button>
      </div>
      <div class="map-toolbar-divider"></div>
      <div class="map-toolbar-section">
        <label>Kategorie</label>
        <div class="dropdown-check" id="filter-category">
          <button type="button" class="dropdown-check-btn">Alle</button>
          <div class="dropdown-check-panel">
            <label class="dropdown-check-item"><input type="checkbox" value="PRAXIS" /> Praxis</label>
            <label class="dropdown-check-item"><input type="checkbox" value="KRANKENHAUS" /> Krankenhaus</label>
          </div>
        </div>
      </div>
      <div class="map-toolbar-section">
        <label>Fachbereich</label>
        <div class="dropdown-check" id="filter-department">
          <button type="button" class="dropdown-check-btn">Alle</button>
          <div class="dropdown-check-panel">
            ${deptCheckboxes}
          </div>
        </div>
      </div>
    </div>

    <div class="map-body">
      <div class="map-container" id="map-container"></div>
      <div class="map-sidebar">
        <div class="map-list-header">Praktikumsstellen</div>
        <div id="map-center-info" class="map-center-info" hidden></div>
        <div id="map-practice-list" class="map-practice-list"></div>
      </div>
    </div>
  `;

  // Init map
  const mapContainer = el.querySelector("#map-container") as HTMLElement;
  initMap(mapContainer);

  // Initial render
  updateMarkers();
  updateSidebar(el);

  // Toggle: Schüler / Klassen
  const modeStudent = el.querySelector("#mode-student") as HTMLButtonElement;
  const modeClasses = el.querySelector("#mode-classes") as HTMLButtonElement;
  const studentSection = el.querySelector("#search-student-section") as HTMLElement;
  const classesSection = el.querySelector("#search-classes-section") as HTMLElement;

  const switchMode = (next: Mode) => {
    if (mode === next) return;
    clearSelection(el);
    mode = next;
    modeStudent.classList.toggle("active", next === "student");
    modeClasses.classList.toggle("active", next === "classes");
    studentSection.style.display = next === "student" ? "flex" : "none";
    classesSection.style.display = next === "classes" ? "flex" : "none";
    updateSearch(el);
  };
  modeStudent.addEventListener("click", () => switchMode("student"));
  modeClasses.addEventListener("click", () => switchMode("classes"));

  // Student dropdown auto-trigger
  el.querySelector("#search-student")!.addEventListener("change", () => {
    const idStr = (el.querySelector("#search-student") as HTMLSelectElement).value;
    const s = studentsWithGeo.find(st => String(st.id) === idStr);
    if (s) {
      focusStudent(s, el);
    } else {
      focusToken++;
      searchLat = null; searchLng = null;
      wishPracticeIds.clear();
      updateSearch(el);
    }
  });

  setupClassFilters(el);

  // Infozeile: Details zur Stelle oder Umkreis aufheben
  el.querySelector("#map-center-info")!.addEventListener("click", (e) => {
    const act = (e.target as HTMLElement).closest("[data-act]") as HTMLElement | null;
    if (!act || !centerPractice) return;
    if (act.dataset.act === "details") showPracticeDetail(centerPractice);
    else { centerPractice = null; updateSearch(el); }
  });

  // Clear search
  el.querySelector("#btn-clear-search")!.addEventListener("click", () => {
    clearSelection(el);
    updateSearch(el);
  });

  // Range input live update
  el.querySelector("#search-range")!.addEventListener("change", () => {
    rangeKm = readRange(el);
    if (searchLat !== null || centerPractice) updateSearch(el);
  });

  // Filters
  setupFilters(el, ac.signal);
}
